
/**
 * Helper functions for generating mock data
 */

import { User } from '@/types/userRole';

// Generate a random ID
export const generateId = (): string => {
  return `id_${Math.random().toString(36).substr(2, 9)}`;
};

// Format date to ISO string
export const formatDate = (date: Date): string => {
  return date.toISOString();
};

// Generate a random name
export const generateName = (): string => {
  const firstNames = [
    'John', 'Mary', 'James', 'Patricia', 'Michael', 'Linda', 
    'Robert', 'Barbara', 'William', 'Elizabeth', 'David', 'Jennifer'
  ];
  
  const lastNames = [
    'Smith', 'Johnson', 'Williams', 'Jones', 'Brown', 'Davis',
    'Miller', 'Wilson', 'Moore', 'Taylor', 'Anderson', 'Thomas'
  ];
  
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  
  return `${firstName} ${lastName}`;
};

// Generate a random email based on name
export const generateEmail = (name?: string): string => {
  const domains = ['example.com', 'test.org', 'mock.net', 'sample.io', 'fake.com'];
  const domain = domains[Math.floor(Math.random() * domains.length)];
  
  if (name) {
    // Convert name to email-friendly format
    const emailName = name.toLowerCase().replace(/\s+/g, '.');
    return `${emailName}@${domain}`;
  } else {
    // Generate random email
    const randomString = Math.random().toString(36).substring(2, 8);
    return `user.${randomString}@${domain}`;
  }
};

// Generate random date within the past year
export const generatePastDate = (maxDaysAgo: number = 365): Date => {
  const now = new Date();
  const daysAgo = Math.floor(Math.random() * maxDaysAgo);
  const pastDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
  return pastDate;
};

// These user generation functions are deprecated - use Supabase authentication instead

// Generate mock monthly report data
export const generateMockMonthlyReport = () => {
  // Generate chart data for the past 6 months
  const chartData = [];
  const now = new Date();
  
  for (let i = 5; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = monthDate.toLocaleString('default', { month: 'short' });
    
    chartData.push({
      name: month,
      referrals: Math.floor(Math.random() * 50) + 10,
      conversions: Math.floor(Math.random() * 30),
      earnings: Math.floor(Math.random() * 2000) + 500
    });
  }
  
  // Generate mock report data
  const reportData = [];
  for (let i = 0; i < 20; i++) {
    const referrerName = generateName();
    reportData.push({
      id: generateId(),
      referrer: referrerName,
      referrerEmail: generateEmail(referrerName),
      count: Math.floor(Math.random() * 20) + 1,
      conversions: Math.floor(Math.random() * 10),
      earnings: Math.floor(Math.random() * 1000) + 100,
      date: formatDate(generatePastDate(30))
    });
  }
  
  return { chartData, data: reportData };
};

// Generate mock super admins
export const mockSuperAdmins = Array(10).fill(null).map((_, index) => {
  const name = generateName();
  return {
    id: `sa-${index+1}`,
    name,
    email: generateEmail(name),
    domain: `domain-${index+1}.com`,
    status: index % 3 === 0 ? 'active' : (index % 3 === 1 ? 'pending' : 'suspended'),
    createdAt: generatePastDate(90),
    licenseKeyId: index % 2 === 0 ? `lk-${index+1}` : null
  };
});

